import React, { useMemo } from 'react'
import {
  ModifyPlanBoxSummaryList,
  ModifyPlanBoxSummarySeparator,
  ModifyPlanBoxSummaryStyled,
  ModifyPlanBoxSummaryTitle,
  ModifyPlanBoxSummaryTitleText,
} from './ModifyPlanBoxSummary.styled'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useGetCheckoutSummary } from 'Features/CrossSell/CrosSellPricing/api/useGetCheckoutSummary'
import { CheckoutSummaryResponseData } from 'Types/CheckoutSummaryResponseType'
import { ModifyPlanBoxSummaryRow } from 'Features/CrossSell/ModifyPlan/ModifyPlanBoxSummary/ModifyPlanBoxRow/ModifyPlanBoxSummaryRow'
import { ComponentLoader, Tooltip, theme } from '@clickcease/cc-client-components-lib'
import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { useUserStore } from 'Stores/userStoreModuleFed'
import {
  InstallmentsSubscriptionWrapper,
  PaymentPlanTitle,
} from 'Features/CrossSell/CrosSellPricing/CrossSellPlansBox/CrossSellPlansBox.styled'
import { StringMap } from 'i18next'
import { Plan } from 'Features/pricing/types/Plan'
import { Products } from 'Types/Products'

type ModifyPlanBoxSummaryProps = {
  plans?: Plan[]
  isCrossSell?: boolean
}

export const ModifyPlanBoxSummary = ({ plans, isCrossSell }: ModifyPlanBoxSummaryProps) => {
  const { t } = useTranslation('pricing')
  const planIds = plans?.map((plan) => plan.id)
  const queryData = useGetCheckoutSummary(planIds || [])
  const { isInstallmentsSubscription } = useUserStore()

  const summaryData: CheckoutSummaryResponseData | undefined = useMemo(() => {
    if (queryData && queryData.data) {
      return queryData.data.data
    }
  }, [queryData])

  const mapProductType = (productType) => {
    switch (productType) {
      case Products.BOT_ZAPPING:
        return 'bot_mitigation'
      case Products.PAID_MARKETING:
        return 'paid_marketing'
    }
  }
  const matchedPlans = plans
    ?.filter((plan) => planIds?.includes(plan.id))
    .map((match) => ({
      productType: mapProductType(match.productType),
      amount: summaryData?.individualCost.find((cost) => cost.productId === match.id)?.amount || 0,
    }))

  const [isOpen, setIsOpen] = React.useState<boolean>(false)

  const translate = (key: string, options?: StringMap) => t(`modify_plan.${key}`, options)

  const amountToPayText: string = (() => {
    let amount = ''
    if (isCrossSell && summaryData && summaryData.netAmountToPay >= 0) {
      amount = t('modify_plan.amount_to_pay_trial')
    } else if (summaryData && summaryData.netAmountToPay >= 0) {
      amount = t('modify_plan.amount_to_pay')
    } else if (summaryData && summaryData.netAmountToPay < 0) {
      amount = t('modify_plan.amount_credited')
    } else {
      amount = t('modify_plan.amount_to_pay')
    }
    return amount
  })()

  const separatorAdjustments =
    summaryData &&
    (summaryData?.discount ||
      summaryData?.alreadyPaid > 0 ||
      summaryData?.priceAdjustment ||
      summaryData?.alreadyPaid > 0 ||
      summaryData?.accountBalance > 0)

  const totalToPay =
    summaryData && summaryData?.netAmountToPay
      ? Math.round(Math.abs(summaryData.netAmountToPay))
      : 0

  const totalMonthlyForInstallments = isInstallmentsSubscription
    ? summaryData?.discount
      ? summaryData?.totalSubscriptionCost - summaryData?.discount
      : summaryData?.totalSubscriptionCost || 0
    : 0

  return (
    <ModifyPlanBoxSummaryStyled>
      <ModifyPlanBoxSummaryTitle onClick={() => setIsOpen(!isOpen)}>
        <ModifyPlanBoxSummaryTitleText>
          {translate('your_pricing_summary')}
        </ModifyPlanBoxSummaryTitleText>
        <FontAwesomeIcon
          icon={isOpen ? (faChevronUp as IconProp) : (faChevronDown as IconProp)}
          style={{ marginTop: '3px' }}
        />
      </ModifyPlanBoxSummaryTitle>

      {isOpen && (
        <>
          {queryData.isFetching ? (
            <ComponentLoader css={{ width: '100px', height: '100px', position: 'absolute' }} />
          ) : (
            <ModifyPlanBoxSummaryList>
              {matchedPlans &&
                matchedPlans.map((matchedPlan, index) => (
                  <ModifyPlanBoxSummaryRow
                    key={index}
                    text={translate(matchedPlan?.productType || '')}
                    amount={matchedPlan.amount}
                  />
                ))}
              <ModifyPlanBoxSummarySeparator />
              <ModifyPlanBoxSummaryRow
                text={
                  !isInstallmentsSubscription
                    ? translate('sub_total')
                    : translate('sub_total_installments')
                }
                amount={summaryData?.totalSubscriptionCost || 0}
              />
              {summaryData?.totalSubscriptionCost && <ModifyPlanBoxSummarySeparator />}
              {summaryData && summaryData?.discount > 0 && (
                <ModifyPlanBoxSummaryRow
                  isNegative={true}
                  css={{ color: theme.colors.success.value }}
                  text={translate('discount')}
                  amount={Math.round(summaryData?.discount)}
                />
              )}
              {summaryData && summaryData?.priceAdjustment !== 0 && (
                <ModifyPlanBoxSummaryRow
                  isNegative={summaryData?.priceAdjustment >= 0}
                  text={
                    summaryData?.priceAdjustment >= 0
                      ? translate('price_adjustment')
                      : translate('price_adjustment_credit')
                  }
                  amount={Math.abs(Math.round(summaryData?.priceAdjustment)) || 0}
                />
              )}
              {summaryData && summaryData?.alreadyPaid !== 0 && (
                <ModifyPlanBoxSummaryRow
                  isNegative={true}
                  text={translate('amount_already_paid')}
                  amount={summaryData?.alreadyPaid || 0}
                />
              )}

              {summaryData && summaryData?.accountBalance > 0 && (
                <ModifyPlanBoxSummaryRow
                  spaceAfter={true}
                  isNegative={true}
                  text={translate('account_credit')}
                  amount={summaryData?.accountBalance}
                />
              )}
              {separatorAdjustments && <ModifyPlanBoxSummarySeparator />}

              <ModifyPlanBoxSummaryRow
                outstanding={true}
                outstandingPrice={true}
                text={amountToPayText}
                amount={totalToPay}
              />

              {isInstallmentsSubscription && (
                <>
                  <ModifyPlanBoxSummarySeparator />
                  <ModifyPlanBoxSummaryRow
                    amount={totalMonthlyForInstallments}
                    text={translate('installments')}
                  />
                  <InstallmentsSubscriptionWrapper>
                    <PaymentPlanTitle>
                      {translate('payment_plan_next_year')}
                      <Tooltip
                        tooltiptContent={
                          <>
                            {t('modify_plan.one_time_fee')}
                            <br />
                            {t('modify_plan.fee_charged')}
                          </>
                        }
                        trigerStyle={{
                          style: {
                            color: theme.colors.gray.value,
                            marginLeft: '4px',
                            position: 'relative',
                            top: '3px',
                          },
                          width: '16px',
                        }}
                      ></Tooltip>
                    </PaymentPlanTitle>
                    <ModifyPlanBoxSummaryRow
                      amount={Math.round(summaryData?.nextYearOneTimeFee || 0)}
                      text={''}
                    />
                  </InstallmentsSubscriptionWrapper>
                  <ModifyPlanBoxSummarySeparator />
                  <ModifyPlanBoxSummaryRow
                    amount={Math.round(summaryData?.nextYearTotalCost || 0)}
                    text={t('modify_plan.next_year_total')}
                  />
                </>
              )}
            </ModifyPlanBoxSummaryList>
          )}
        </>
      )}
    </ModifyPlanBoxSummaryStyled>
  )
}
